import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  CardActionArea
} from '@mui/material';
import {
  Save,
  Refresh,
  Settings,
  Palette,
  Add,
  Delete,
  Edit,
  Web,
  Dashboard,
  Assessment,
  Chat,
  People,
  AccessTime,
  Code,
  Menu as MenuIcon,
  Security,
  Speed,
  Notifications,
  Language,
  ViewModule,
  Storage,
  CloudUpload,
  CheckCircle,
  Error,
  Info
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSettings, AppSettings } from '../contexts/SettingsContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import dataConnectionService from '../services/dataConnectionService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// PageSettings interface'i artık AppSettings ile aynı

const PageSettings: React.FC = () => {
  const { hasRole, hasPermission } = useAuth();
  const { settings, updateSetting, resetSettings, saveSettings } = useSettings();
  const { mode } = useCustomTheme();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [isMenuDialogOpen, setIsMenuDialogOpen] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<any>(null);

  // Rol kontrolü - sadece Admin ve Developer erişebilir
  if (!hasRole(['admin', 'developer'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır. Sadece Admin ve Developer kullanıcıları bu sayfayı görüntüleyebilir.
        </Alert>
      </Container>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSettingChange = (key: keyof AppSettings, value: any) => {
    // Anlık olarak settings context'ine kaydet
    updateSetting(key, value);
  };

  const handleSaveSettings = () => {
    // Settings zaten anlık kaydediliyor, sadece feedback ver
    saveSettings();
    setSnackbar({
      open: true,
      message: 'Ayarlar başarıyla kaydedildi!',
      severity: 'success'
    });
  };

  const handleResetSettings = () => {
    resetSettings();
    setSnackbar({
      open: true,
      message: 'Ayarlar varsayılan değerlere sıfırlandı!',
      severity: 'info'
    });
  };

  const handleAddMenuItem = () => {
    setEditingMenuItem({
      id: '',
      title: '',
      icon: '',
      path: '',
      visible: true,
      order: settings.menuItems.length + 1
    });
    setIsMenuDialogOpen(true);
  };

  const handleEditMenuItem = (item: any) => {
    setEditingMenuItem(item);
    setIsMenuDialogOpen(true);
  };

  const handleDeleteMenuItem = (itemId: string) => {
    const updatedMenuItems = settings.menuItems.filter(item => item.id !== itemId);
    updateSetting('menuItems', updatedMenuItems);
  };

  const handleSaveMenuItem = () => {
    if (editingMenuItem) {
      if (editingMenuItem.id && settings.menuItems.find(item => item.id === editingMenuItem.id)) {
        // Güncelle
        const updatedMenuItems = settings.menuItems.map(item => 
          item.id === editingMenuItem.id ? editingMenuItem : item
        );
        updateSetting('menuItems', updatedMenuItems);
      } else {
        // Yeni ekle
        const newId = editingMenuItem.title.toLowerCase().replace(/\s+/g, '-');
        const updatedMenuItems = [...settings.menuItems, { ...editingMenuItem, id: newId }];
        updateSetting('menuItems', updatedMenuItems);
      }
      
      setIsMenuDialogOpen(false);
      setEditingMenuItem(null);
    }
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: { [key: string]: React.ComponentType } = {
      Dashboard,
      Assessment,
      AccessTime,
      Chat,
      People,
      Web,
      Code,
      Menu: MenuIcon
    };
    return iconMap[iconName] || Dashboard;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        p: 3,
        mb: 3,
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              ⚙️ Sayfa Ayarları
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Sistem genelinde sayfa özelliklerini yönetin ve özelleştirin.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleResetSettings}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Sıfırla
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSaveSettings}
              sx={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease'
                }
              }}
            >
              Kaydet
            </Button>
          </Box>
        </Box>
      </Box>

       {/* Settings Tabs */}
       <Card sx={{
         backgroundColor: mode === 'dark' ? settings.darkModeCardBackground : settings.lightModeCardBackground,
         color: mode === 'dark' ? settings.darkModeTextColor : settings.lightModeTextColor,
         borderRadius: 3
       }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab icon={<Settings />} label="Genel" />
            <Tab icon={<Palette />} label="Tema" />
            <Tab icon={<ViewModule />} label="Layout" />
            <Tab icon={<Dashboard />} label="Dashboard" />
            <Tab icon={<Assessment />} label="Raporlar" />
            <Tab icon={<Chat />} label="Chat" />
            <Tab icon={<Security />} label="Güvenlik" />
            <Tab icon={<Speed />} label="Performans" />
            <Tab icon={<Notifications />} label="Bildirimler" />
            <Tab icon={<Language />} label="Dil & Bölge" />
            <Tab icon={<Code />} label="Özel Kod" />
            <Tab icon={<Storage />} label="Data Bağlantısı" />
            {hasPermission('panel_settings') && (
              <Tab icon={<Web />} label="Panel" />
            )}
            <Tab icon={<MenuIcon />} label="Menü" />
          </Tabs>
        </Box>

        {/* Genel Ayarlar */}
        <TabPanel value={tabValue} index={0}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Genel Site Ayarları
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <TextField
                fullWidth
                label="Site Adı"
                value={settings.siteName}
                onChange={(e) => handleSettingChange('siteName', e.target.value)}
              />
              <TextField
                fullWidth
                label="Site Açıklaması"
                value={settings.siteDescription}
                onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
              />
              <TextField
                fullWidth
                label="Site Logo URL"
                value={settings.siteLogo}
                onChange={(e) => handleSettingChange('siteLogo', e.target.value)}
              />
              <TextField
                fullWidth
                label="Favicon URL"
                value={settings.favicon}
                onChange={(e) => handleSettingChange('favicon', e.target.value)}
              />
            </Box>

            <Divider />

            <Typography variant="h6" fontWeight="bold">
              Bakım Modu
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.maintenanceMode}
                  onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                />
              }
              label="Bakım modunu etkinleştir"
            />
            
            {settings.maintenanceMode && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Bakım Mesajı"
                value={settings.maintenanceMessage}
                onChange={(e) => handleSettingChange('maintenanceMessage', e.target.value)}
              />
            )}
          </Stack>
        </TabPanel>

        {/* Tema Ayarları */}
        <TabPanel value={tabValue} index={1}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Renk Paleti
            </Typography>
            
             <Box sx={{ 
               display: 'grid', 
               gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
               gap: 3 
             }}>
               <TextField
                 fullWidth
                 type="color"
                 label="Ana Renk"
                 value={settings.primaryColor}
                 onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
               />
               <TextField
                 fullWidth
                 type="color"
                 label="İkincil Renk"
                 value={settings.secondaryColor}
                 onChange={(e) => handleSettingChange('secondaryColor', e.target.value)}
               />
             </Box>

             <Divider />

             <Typography variant="h6" fontWeight="bold">
               Açık Mod Renkleri
             </Typography>
             
             <Box sx={{ 
               display: 'grid', 
               gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
               gap: 3 
             }}>
               <TextField
                 fullWidth
                 type="color"
                 label="Açık Mod Arka Plan"
                 value={settings.lightModeBackground}
                 onChange={(e) => handleSettingChange('lightModeBackground', e.target.value)}
               />
               <TextField
                 fullWidth
                 type="color"
                 label="Açık Mod Metin Rengi"
                 value={settings.lightModeTextColor}
                 onChange={(e) => handleSettingChange('lightModeTextColor', e.target.value)}
               />
               <TextField
                 fullWidth
                 type="color"
                 label="Açık Mod Kart Arka Planı"
                 value={settings.lightModeCardBackground}
                 onChange={(e) => handleSettingChange('lightModeCardBackground', e.target.value)}
               />
             </Box>

             <Divider />

             <Typography variant="h6" fontWeight="bold">
               Koyu Mod Renkleri
             </Typography>
             
             <Box sx={{ 
               display: 'grid', 
               gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
               gap: 3 
             }}>
               <TextField
                 fullWidth
                 type="color"
                 label="Koyu Mod Arka Plan"
                 value={settings.darkModeBackground}
                 onChange={(e) => handleSettingChange('darkModeBackground', e.target.value)}
               />
               <TextField
                 fullWidth
                 type="color"
                 label="Koyu Mod Metin Rengi"
                 value={settings.darkModeTextColor}
                 onChange={(e) => handleSettingChange('darkModeTextColor', e.target.value)}
               />
               <TextField
                 fullWidth
                 type="color"
                 label="Koyu Mod Kart Arka Planı"
                 value={settings.darkModeCardBackground}
                 onChange={(e) => handleSettingChange('darkModeCardBackground', e.target.value)}
               />
             </Box>

            <Divider />

            <Typography variant="h6" fontWeight="bold">
              Görsel Stil
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <Box>
                <Typography gutterBottom>Köşe Yuvarlaklığı</Typography>
                <Slider
                  value={settings.borderRadius}
                  onChange={(e, value) => handleSettingChange('borderRadius', value)}
                  min={0}
                  max={20}
                  step={1}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
              <TextField
                fullWidth
                label="Gölge Stili"
                value={settings.boxShadow}
                onChange={(e) => handleSettingChange('boxShadow', e.target.value)}
              />
            </Box>
          </Stack>
        </TabPanel>

        {/* Layout Ayarları */}
        <TabPanel value={tabValue} index={2}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Layout Boyutları
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <Box>
                <Typography gutterBottom>Sidebar Genişliği (px)</Typography>
                <Slider
                  value={settings.sidebarWidth}
                  onChange={(e, value) => handleSettingChange('sidebarWidth', value)}
                  min={200}
                  max={400}
                  step={10}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
              <Box>
                <Typography gutterBottom>Header Yüksekliği (px)</Typography>
                <Slider
                  value={settings.headerHeight}
                  onChange={(e, value) => handleSettingChange('headerHeight', value)}
                  min={48}
                  max={120}
                  step={8}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
              <Box>
                <Typography gutterBottom>Footer Yüksekliği (px)</Typography>
                <Slider
                  value={settings.footerHeight}
                  onChange={(e, value) => handleSettingChange('footerHeight', value)}
                  min={32}
                  max={80}
                  step={8}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
              <Box>
                <Typography gutterBottom>İçerik Padding (px)</Typography>
                <Slider
                  value={settings.contentPadding}
                  onChange={(e, value) => handleSettingChange('contentPadding', value)}
                  min={8}
                  max={200}
                  step={4}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>
            </Box>
          </Stack>
        </TabPanel>

        {/* Dashboard Ayarları */}
        <TabPanel value={tabValue} index={3}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Dashboard Bileşenleri
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showWelcomeMessage}
                    onChange={(e) => handleSettingChange('showWelcomeMessage', e.target.checked)}
                  />
                }
                label="Hoş geldin mesajını göster"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showQuickActions}
                    onChange={(e) => handleSettingChange('showQuickActions', e.target.checked)}
                  />
                }
                label="Hızlı işlemler kartlarını göster"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showStatistics}
                    onChange={(e) => handleSettingChange('showStatistics', e.target.checked)}
                  />
                }
                label="İstatistik kartlarını göster"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showRecentActivities}
                    onChange={(e) => handleSettingChange('showRecentActivities', e.target.checked)}
                  />
                }
                label="Son aktiviteleri göster"
              />
            </FormGroup>
            
            {settings.showWelcomeMessage && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Hoş Geldin Mesajı"
                value={settings.welcomeMessage}
                onChange={(e) => handleSettingChange('welcomeMessage', e.target.value)}
              />
            )}
          </Stack>
        </TabPanel>

        {/* Rapor Ayarları */}
        <TabPanel value={tabValue} index={4}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Rapor Yönetimi
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <FormControl fullWidth>
                <InputLabel>Varsayılan Rapor Türü</InputLabel>
                <Select
                  value={settings.defaultReportType}
                  onChange={(e) => handleSettingChange('defaultReportType', e.target.value)}
                  label="Varsayılan Rapor Türü"
                >
                  <MenuItem value="daily">Günlük</MenuItem>
                  <MenuItem value="monthly">Aylık</MenuItem>
                  <MenuItem value="sales">Satış</MenuItem>
                  <MenuItem value="call">Call</MenuItem>
                  <MenuItem value="livechat">LiveChat</MenuItem>
                  <MenuItem value="risk">Risk</MenuItem>
                  <MenuItem value="hourly">Saatlik</MenuItem>
                  <MenuItem value="work_summary">Mesai Özeti</MenuItem>
                  <MenuItem value="weekly">Haftalık</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                type="number"
                label="Rapor Saklama Süresi (Gün)"
                value={settings.reportRetentionDays}
                onChange={(e) => handleSettingChange('reportRetentionDays', parseInt(e.target.value))}
              />
            </Box>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoSaveReports}
                    onChange={(e) => handleSettingChange('autoSaveReports', e.target.checked)}
                  />
                }
                label="Raporları otomatik kaydet"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowBulkDelete}
                    onChange={(e) => handleSettingChange('allowBulkDelete', e.target.checked)}
                  />
                }
                label="Toplu silme işlemine izin ver"
              />
            </FormGroup>
          </Stack>
        </TabPanel>

        {/* Chat Ayarları */}
        <TabPanel value={tabValue} index={5}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Canlı Chat Ayarları
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableChat}
                    onChange={(e) => handleSettingChange('enableChat', e.target.checked)}
                  />
                }
                label="Canlı chat'i etkinleştir"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowFileUpload}
                    onChange={(e) => handleSettingChange('allowFileUpload', e.target.checked)}
                  />
                }
                label="Dosya yüklemeye izin ver"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableNotifications}
                    onChange={(e) => handleSettingChange('enableNotifications', e.target.checked)}
                  />
                }
                label="Bildirimleri etkinleştir"
              />
            </FormGroup>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <TextField
                fullWidth
                type="number"
                label="Maksimum Dosya Boyutu (MB)"
                value={settings.maxFileSize}
                onChange={(e) => handleSettingChange('maxFileSize', parseInt(e.target.value))}
              />
              <TextField
                fullWidth
                type="number"
                label="Chat Geçmişi Saklama (Gün)"
                value={settings.chatHistoryDays}
                onChange={(e) => handleSettingChange('chatHistoryDays', parseInt(e.target.value))}
              />
            </Box>
          </Stack>
        </TabPanel>

        {/* Güvenlik Ayarları */}
        <TabPanel value={tabValue} index={6}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Güvenlik Ayarları
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <TextField
                fullWidth
                type="number"
                label="Oturum Zaman Aşımı (Dakika)"
                value={settings.sessionTimeout}
                onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
              />
              <TextField
                fullWidth
                type="number"
                label="Maksimum Giriş Denemesi"
                value={settings.maxLoginAttempts}
                onChange={(e) => handleSettingChange('maxLoginAttempts', parseInt(e.target.value))}
              />
              <TextField
                fullWidth
                type="number"
                label="Minimum Şifre Uzunluğu"
                value={settings.passwordMinLength}
                onChange={(e) => handleSettingChange('passwordMinLength', parseInt(e.target.value))}
              />
            </Box>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireTwoFactor}
                    onChange={(e) => handleSettingChange('requireTwoFactor', e.target.checked)}
                  />
                }
                label="İki faktörlü kimlik doğrulama zorunlu"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.passwordRequireSpecial}
                    onChange={(e) => handleSettingChange('passwordRequireSpecial', e.target.checked)}
                  />
                }
                label="Şifrede özel karakter zorunlu"
              />
            </FormGroup>
          </Stack>
        </TabPanel>

        {/* Performans Ayarları */}
        <TabPanel value={tabValue} index={7}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Performans Optimizasyonu
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableCaching}
                    onChange={(e) => handleSettingChange('enableCaching', e.target.checked)}
                  />
                }
                label="Önbellekleme etkinleştir"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableCompression}
                    onChange={(e) => handleSettingChange('enableCompression', e.target.checked)}
                  />
                }
                label="Sıkıştırma etkinleştir"
              />
            </FormGroup>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <TextField
                fullWidth
                type="number"
                label="Önbellek Süresi (Saniye)"
                value={settings.cacheExpiration}
                onChange={(e) => handleSettingChange('cacheExpiration', parseInt(e.target.value))}
              />
              <TextField
                fullWidth
                type="number"
                label="Maksimum Yükleme Boyutu (MB)"
                value={settings.maxUploadSize}
                onChange={(e) => handleSettingChange('maxUploadSize', parseInt(e.target.value))}
              />
            </Box>
          </Stack>
        </TabPanel>

        {/* Bildirim Ayarları */}
        <TabPanel value={tabValue} index={8}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Bildirim Tercihleri
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  />
                }
                label="E-posta bildirimleri"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.pushNotifications}
                    onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                  />
                }
                label="Push bildirimleri"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.smsNotifications}
                    onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                  />
                }
                label="SMS bildirimleri"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notificationSound}
                    onChange={(e) => handleSettingChange('notificationSound', e.target.checked)}
                  />
                }
                label="Bildirim sesi"
              />
            </FormGroup>
          </Stack>
        </TabPanel>

        {/* Dil ve Bölge */}
        <TabPanel value={tabValue} index={9}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Dil ve Bölge Ayarları
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              <FormControl fullWidth>
                <InputLabel>Varsayılan Dil</InputLabel>
                <Select
                  value={settings.defaultLanguage}
                  onChange={(e) => handleSettingChange('defaultLanguage', e.target.value)}
                  label="Varsayılan Dil"
                >
                  <MenuItem value="tr">Türkçe</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="de">Deutsch</MenuItem>
                  <MenuItem value="fr">Français</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Saat Dilimi</InputLabel>
                <Select
                  value={settings.timezone}
                  onChange={(e) => handleSettingChange('timezone', e.target.value)}
                  label="Saat Dilimi"
                >
                  <MenuItem value="Europe/Istanbul">İstanbul (UTC+3)</MenuItem>
                  <MenuItem value="Europe/London">Londra (UTC+0)</MenuItem>
                  <MenuItem value="America/New_York">New York (UTC-5)</MenuItem>
                  <MenuItem value="Asia/Tokyo">Tokyo (UTC+9)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Tarih Formatı</InputLabel>
                <Select
                  value={settings.dateFormat}
                  onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                  label="Tarih Formatı"
                >
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                  <MenuItem value="DD-MM-YYYY">DD-MM-YYYY</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Saat Formatı</InputLabel>
                <Select
                  value={settings.timeFormat}
                  onChange={(e) => handleSettingChange('timeFormat', e.target.value)}
                  label="Saat Formatı"
                >
                  <MenuItem value="24">24 Saat</MenuItem>
                  <MenuItem value="12">12 Saat (AM/PM)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Para Birimi</InputLabel>
                <Select
                  value={settings.currency}
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                  label="Para Birimi"
                >
                  <MenuItem value="TRY">₺ Türk Lirası</MenuItem>
                  <MenuItem value="USD">$ Amerikan Doları</MenuItem>
                  <MenuItem value="EUR">€ Euro</MenuItem>
                  <MenuItem value="GBP">£ İngiliz Sterlini</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </TabPanel>

        {/* Özel Kod */}
        <TabPanel value={tabValue} index={10}>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Özel CSS ve JavaScript
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={10}
              label="Özel CSS"
              value={settings.customCSS}
              onChange={(e) => handleSettingChange('customCSS', e.target.value)}
              placeholder="/* Özel CSS kodlarınızı buraya yazın */"
            />
            
            <TextField
              fullWidth
              multiline
              rows={10}
              label="Özel JavaScript"
              value={settings.customJavaScript}
              onChange={(e) => handleSettingChange('customJavaScript', e.target.value)}
              placeholder="// Özel JavaScript kodlarınızı buraya yazın"
            />
          </Stack>
        </TabPanel>

        {/* Menü Ayarları */}
        <TabPanel value={tabValue} index={12}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                Menü Öğeleri
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddMenuItem}
              >
                Yeni Menü Öğesi
              </Button>
            </Box>
            
            <List>
              {settings.menuItems.map((item, index) => {
                const IconComponent = getIconComponent(item.icon);
                return (
                  <ListItem key={item.id}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconComponent />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.title}
                          </Typography>
                          <Chip label={item.path} size="small" />
                          <Chip 
                            label={item.visible ? 'Görünür' : 'Gizli'} 
                            size="small" 
                            color={item.visible ? 'success' : 'default'}
                          />
                        </Box>
                      }
                      secondary={`Sıra: ${item.order}`}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Düzenle">
                        <IconButton onClick={() => handleEditMenuItem(item)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton onClick={() => handleDeleteMenuItem(item.id)} color="error">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </Stack>
        </TabPanel>

        {/* Data Bağlantısı */}
        <TabPanel value={tabValue} index={11}>
          <DataConnectionPanel />
        </TabPanel>

        {/* Panel Ayarları */}
        {hasPermission('panel_settings') && (
          <TabPanel value={tabValue} index={12}>
            <PanelSettingsPanel />
          </TabPanel>
        )}
      </Card>

      {/* Menü Öğesi Düzenleme Dialog */}
       <Dialog open={isMenuDialogOpen} onClose={() => setIsMenuDialogOpen(false)} maxWidth="sm" fullWidth>
         <DialogTitle>
           {editingMenuItem?.id ? 'Menü Öğesi Düzenle' : 'Yeni Menü Öğesi'}
         </DialogTitle>
        
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Başlık"
              value={editingMenuItem?.title || ''}
              onChange={(e) => setEditingMenuItem({ ...editingMenuItem, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="İkon Adı"
              value={editingMenuItem?.icon || ''}
              onChange={(e) => setEditingMenuItem({ ...editingMenuItem, icon: e.target.value })}
              placeholder="Dashboard, Assessment, Chat, vb."
            />
            <TextField
              fullWidth
              label="Yol"
              value={editingMenuItem?.path || ''}
              onChange={(e) => setEditingMenuItem({ ...editingMenuItem, path: e.target.value })}
              placeholder="/dashboard, /reports/create, vb."
            />
            <TextField
              fullWidth
              type="number"
              label="Sıra"
              value={editingMenuItem?.order || 1}
              onChange={(e) => setEditingMenuItem({ ...editingMenuItem, order: parseInt(e.target.value) })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingMenuItem?.visible || false}
                  onChange={(e) => setEditingMenuItem({ ...editingMenuItem, visible: e.target.checked })}
                />
              }
              label="Görünür"
            />
          </Stack>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setIsMenuDialogOpen(false)}>
            İptal
          </Button>
          <Button
            onClick={handleSaveMenuItem}
            variant="contained"
            startIcon={<Save />}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Data Bağlantısı Paneli
const DataConnectionPanel: React.FC = () => {
  const { settings, updateSetting } = useSettings();
  const { mode } = useCustomTheme();
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [selectedConnectionType, setSelectedConnectionType] = useState<'mongodb' | 'googlesheets' | null>(null);
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);
  const [connectionData, setConnectionData] = useState({
    mongodb: {
      connectionString: settings.dataConnection.mongodb.connectionString,
      databaseName: settings.dataConnection.mongodb.databaseName
    },
    googlesheets: {
      spreadsheetId: settings.dataConnection.googlesheets.spreadsheetId,
      credentials: settings.dataConnection.googlesheets.credentials
    }
  });
  const [connectionStatus, setConnectionStatus] = useState({
    mongodb: settings.dataConnection.mongodb.isConnected,
    googlesheets: settings.dataConnection.googlesheets.isConnected
  });

  const handleConnectionTypeSelect = (type: 'mongodb' | 'googlesheets') => {
    setSelectedConnectionType(type);
    setIsConnectionDialogOpen(false);
    setIsSetupDialogOpen(true);
  };

  const handleConnectionTest = async (type: 'mongodb' | 'googlesheets') => {
    try {
      let result: any;
      
      if (type === 'mongodb') {
        result = await dataConnectionService.testMongoDBConnection(
          connectionData.mongodb.connectionString,
          connectionData.mongodb.databaseName
        );
        
        setConnectionStatus(prev => ({ ...prev, mongodb: result.success }));
        
        if (result.success) {
          updateSetting('dataConnection.mongodb.connectionString', connectionData.mongodb.connectionString);
          updateSetting('dataConnection.mongodb.databaseName', connectionData.mongodb.databaseName);
          updateSetting('dataConnection.mongodb.isConnected', true);
          updateSetting('dataConnection.type', 'mongodb');
          
          // Servis yapılandırmasını ayarla
          dataConnectionService.setConfig({
            type: 'mongodb',
            mongodb: {
              connectionString: connectionData.mongodb.connectionString,
              databaseName: connectionData.mongodb.databaseName
            }
          });
          
          // Başarılı bağlantı sonrası admin setup sayfasına yönlendir
          setTimeout(() => {
            window.location.href = '/admin-setup';
          }, 2000);
        }
      } else {
        result = await dataConnectionService.testGoogleSheetsConnection(
          connectionData.googlesheets.spreadsheetId,
          connectionData.googlesheets.credentials
        );
        
        setConnectionStatus(prev => ({ ...prev, googlesheets: result.success }));
        
        if (result.success) {
          updateSetting('dataConnection.googlesheets.spreadsheetId', connectionData.googlesheets.spreadsheetId);
          updateSetting('dataConnection.googlesheets.credentials', connectionData.googlesheets.credentials);
          updateSetting('dataConnection.googlesheets.isConnected', true);
          updateSetting('dataConnection.type', 'googlesheets');
          
          // Servis yapılandırmasını ayarla
          dataConnectionService.setConfig({
            type: 'googlesheets',
            googlesheets: {
              spreadsheetId: connectionData.googlesheets.spreadsheetId,
              credentials: connectionData.googlesheets.credentials
            }
          });
          
          // Başarılı bağlantı sonrası admin setup sayfasına yönlendir
          setTimeout(() => {
            window.location.href = '/admin-setup';
          }, 2000);
        }
      }
      
      console.log('Connection test result:', result);
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionStatus(prev => ({ ...prev, [type]: false }));
    }
  };

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">
          Veri Bağlantısı Yönetimi
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setIsConnectionDialogOpen(true)}
        >
          Yeni Bağlantı
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Veri bağlantısı kurarak tüm sistem verilerinizi (kullanıcılar, raporlar, chat grupları) 
        harici bir veritabanında saklayabilirsiniz.
      </Alert>

      {/* Mevcut Bağlantılar */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
        {/* MongoDB Bağlantısı */}
        <Card sx={{ p: 3, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Storage sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
            <Box>
              <Typography variant="h6">MongoDB</Typography>
              <Typography variant="body2" color="text.secondary">
                NoSQL Veritabanı
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {connectionStatus.mongodb ? (
              <CheckCircle sx={{ color: 'success.main' }} />
            ) : (
              <Error sx={{ color: 'error.main' }} />
            )}
            <Typography variant="body2">
              {connectionStatus.mongodb ? 'Bağlı' : 'Bağlı Değil'}
            </Typography>
          </Box>

          {settings.dataConnection.mongodb.connectionString && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Veritabanı: {settings.dataConnection.mongodb.databaseName}
            </Typography>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleConnectionTypeSelect('mongodb')}
          >
            {connectionStatus.mongodb ? 'Yeniden Yapılandır' : 'Bağlantı Kur'}
          </Button>
        </Card>

        {/* Google Sheets Bağlantısı */}
        <Card sx={{ p: 3, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h6">Google Sheets</Typography>
              <Typography variant="body2" color="text.secondary">
                Google Tabloları
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {connectionStatus.googlesheets ? (
              <CheckCircle sx={{ color: 'success.main' }} />
            ) : (
              <Error sx={{ color: 'error.main' }} />
            )}
            <Typography variant="body2">
              {connectionStatus.googlesheets ? 'Bağlı' : 'Bağlı Değil'}
            </Typography>
          </Box>

          {settings.dataConnection.googlesheets.spreadsheetId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tablo ID: {settings.dataConnection.googlesheets.spreadsheetId.substring(0, 20)}...
            </Typography>
          )}

          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleConnectionTypeSelect('googlesheets')}
          >
            {connectionStatus.googlesheets ? 'Yeniden Yapılandır' : 'Bağlantı Kur'}
          </Button>
        </Card>
      </Box>

      {/* Bağlantı Türü Seçim Dialog */}
      <Dialog open={isConnectionDialogOpen} onClose={() => setIsConnectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" textAlign="center">
            Veri Bağlantısı Türü Seçin
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
              }}
            >
              <CardActionArea onClick={() => handleConnectionTypeSelect('mongodb')}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Storage sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">MongoDB</Typography>
                      <Typography variant="body2" color="text.secondary">
                        NoSQL Veritabanı - Esnek ve ölçeklenebilir
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • Yüksek performans<br/>
                    • Esnek veri yapısı<br/>
                    • Kolay ölçeklenebilirlik<br/>
                    • JSON tabanlı
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
              }}
            >
              <CardActionArea onClick={() => handleConnectionTypeSelect('googlesheets')}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h6">Google Sheets</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Google Tabloları - Kolay kullanım
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • Kolay erişim<br/>
                    • Görsel veri yönetimi<br/>
                    • Google entegrasyonu<br/>
                    • Ücretsiz kullanım
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConnectionDialogOpen(false)}>İptal</Button>
        </DialogActions>
      </Dialog>

      {/* Bağlantı Kurulum Dialog */}
      <ConnectionSetupDialog
        open={isSetupDialogOpen}
        onClose={() => setIsSetupDialogOpen(false)}
        connectionType={selectedConnectionType}
        connectionData={connectionData}
        setConnectionData={setConnectionData}
        onTestConnection={handleConnectionTest}
        connectionStatus={connectionStatus}
      />
    </Stack>
  );
};

// Bağlantı Kurulum Dialog Komponenti
const ConnectionSetupDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  connectionType: 'mongodb' | 'googlesheets' | null;
  connectionData: any;
  setConnectionData: (data: any) => void;
  onTestConnection: (type: 'mongodb' | 'googlesheets') => void;
  connectionStatus: any;
}> = ({ open, onClose, connectionType, connectionData, setConnectionData, onTestConnection, connectionStatus }) => {
  const [isTesting, setIsTesting] = useState(false);

  if (!connectionType) return null;

  const handleTest = async () => {
    setIsTesting(true);
    await onTestConnection(connectionType);
    setIsTesting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {connectionType === 'mongodb' ? 'MongoDB Bağlantısı' : 'Google Sheets Bağlantısı'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {connectionType === 'mongodb' ? (
            <>
              <Alert severity="info">
                <Typography variant="body2">
                  MongoDB bağlantısı kurmak için aşağıdaki adımları takip edin:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  1. MongoDB Atlas hesabı oluşturun (ücretsiz)<br/>
                  2. Yeni bir cluster oluşturun<br/>
                  3. Database Access'te kullanıcı oluşturun<br/>
                  4. Network Access'te IP adresinizi ekleyin<br/>
                  5. Connect butonuna tıklayıp connection string'i kopyalayın
                </Typography>
              </Alert>
              
              <TextField
                fullWidth
                label="MongoDB Connection String"
                value={connectionData.mongodb.connectionString}
                onChange={(e) => setConnectionData({
                  ...connectionData,
                  mongodb: { ...connectionData.mongodb, connectionString: e.target.value }
                })}
                placeholder="mongodb+srv://username:password@cluster.mongodb.net/"
                multiline
                rows={2}
              />
              
              <TextField
                fullWidth
                label="Veritabanı Adı"
                value={connectionData.mongodb.databaseName}
                onChange={(e) => setConnectionData({
                  ...connectionData,
                  mongodb: { ...connectionData.mongodb, databaseName: e.target.value }
                })}
                placeholder="panel_database"
              />
            </>
          ) : (
            <>
              <Alert severity="info">
                <Typography variant="body2">
                  Google Sheets bağlantısı kurmak için aşağıdaki adımları takip edin:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  1. Google Cloud Console'da proje oluşturun<br/>
                  2. Google Sheets API'yi etkinleştirin<br/>
                  3. Service Account oluşturun<br/>
                  4. JSON key dosyasını indirin<br/>
                  5. Google Sheets'te service account'u paylaşın
                </Typography>
              </Alert>
              
              <TextField
                fullWidth
                label="Google Sheets ID"
                value={connectionData.googlesheets.spreadsheetId}
                onChange={(e) => setConnectionData({
                  ...connectionData,
                  googlesheets: { ...connectionData.googlesheets, spreadsheetId: e.target.value }
                })}
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                helperText="Google Sheets URL'sindeki /d/ ile /edit arasındaki kısım"
              />
              
              <TextField
                fullWidth
                label="Service Account JSON"
                value={connectionData.googlesheets.credentials}
                onChange={(e) => setConnectionData({
                  ...connectionData,
                  googlesheets: { ...connectionData.googlesheets, credentials: e.target.value }
                })}
                placeholder='{"type": "service_account", "project_id": "..."}'
                multiline
                rows={4}
                helperText="Google Cloud Console'dan indirdiğiniz JSON key dosyasının içeriği"
              />
            </>
          )}

          {/* Bağlantı Test Sonucu */}
          {connectionStatus[connectionType] && (
            <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ mr: 1 }} />
              <Typography variant="body2">
                Bağlantı başarıyla kuruldu! Sistem artık {connectionType === 'mongodb' ? 'MongoDB' : 'Google Sheets'} kullanıyor.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleTest}
          disabled={isTesting}
          startIcon={isTesting ? <Info /> : <CheckCircle />}
        >
          {isTesting ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Panel Ayarları Paneli
const PanelSettingsPanel: React.FC = () => {
  const [panelUrl, setPanelUrl] = useState('');
  const [panelTitle, setPanelTitle] = useState('Panel');
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [roleBasedUrls, setRoleBasedUrls] = useState<{[role: string]: {url: string, title: string}}>({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false, message: '', severity: 'info'
  });

  // Tüm roller
  const allRoles = [
    { value: 'admin', label: 'Admin' },
    { value: 'developer', label: 'Developer' },
    { value: 'mod', label: 'Moderator' },
    { value: 'call_admin', label: 'Call Admin' },
    { value: 'call', label: 'Call' },
    { value: 'livechat_admin', label: 'LiveChat Admin' },
    { value: 'livechat', label: 'LiveChat' },
    { value: 'risk_admin', label: 'Risk Admin' },
    { value: 'risk', label: 'Risk' }
  ];

  // Panel ayarlarını yükle
  React.useEffect(() => {
    loadPanelSettings();
  }, []);

  const loadPanelSettings = async () => {
    try {
      setLoading(true);
      const result = await dataConnectionService.getPanelSettings();
      
      if (result.success && result.data) {
        setPanelUrl(result.data.url || '');
        setPanelTitle(result.data.title || 'Panel');
        setAllowNavigation(result.data.allowNavigation || false);
        setRoleBasedUrls(result.data.roleBasedUrls || {});
      }
    } catch (error) {
      console.error('Panel ayarları yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const panelSettings = {
        url: panelUrl.trim(),
        title: panelTitle.trim(),
        allowNavigation,
        roleBasedUrls
      };

      const result = await dataConnectionService.savePanelSettings(panelSettings);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Panel ayarları başarıyla kaydedildi',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Panel ayarları kaydedilemedi: ${result.message}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Panel ayarları kaydetme hatası:', error);
      setSnackbar({
        open: true,
        message: 'Panel ayarları kaydedilemedi',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Rol bazlı URL güncelleme
  const updateRoleUrl = (role: string, field: 'url' | 'title', value: string) => {
    setRoleBasedUrls(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: value
      }
    }));
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" fontWeight="bold">
        Panel Ayarları
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        Panel ayarları tüm kullanıcıların görebileceği web sayfasını belirler. 
        Bu ayarlar tüm kullanıcıları etkiler.
      </Alert>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Genel Panel Ayarları
            </Typography>
            
            <TextField
              fullWidth
              label="Varsayılan Panel URL"
              value={panelUrl}
              onChange={(e) => setPanelUrl(e.target.value)}
              placeholder="https://example.com"
              helperText="Rol bazlı URL tanımlanmamış roller için kullanılacak varsayılan URL"
            />
            
            <TextField
              fullWidth
              label="Varsayılan Panel Başlığı"
              value={panelTitle}
              onChange={(e) => setPanelTitle(e.target.value)}
              placeholder="Panel"
              helperText="Rol bazlı başlık tanımlanmamış roller için kullanılacak varsayılan başlık"
            />
            
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowNavigation}
                    onChange={(e) => setAllowNavigation(e.target.checked)}
                  />
                }
                label="Navigasyona İzin Ver"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                Kullanıcıların panel içinde navigasyon yapmasına izin ver
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="bold">
              Rol Bazlı Panel Ayarları
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Her rol için özel panel URL'i ve başlığı tanımlayabilirsiniz. 
              Tanımlanmamış roller varsayılan ayarları kullanır.
            </Alert>

            {allRoles.map((role) => (
              <Card key={role.value} variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {role.label} Paneli
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label={`${role.label} Panel URL'i`}
                      value={roleBasedUrls[role.value]?.url || ''}
                      onChange={(e) => updateRoleUrl(role.value, 'url', e.target.value)}
                      placeholder="https://example.com"
                      helperText={`${role.label} rolündeki kullanıcılar için özel panel URL'i`}
                    />
                    
                    <TextField
                      fullWidth
                      label={`${role.label} Panel Başlığı`}
                      value={roleBasedUrls[role.value]?.title || ''}
                      onChange={(e) => updateRoleUrl(role.value, 'title', e.target.value)}
                      placeholder={`${role.label} Paneli`}
                      helperText={`${role.label} rolündeki kullanıcılar için özel panel başlığı`}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Box display="flex" gap={2}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !panelUrl.trim()}
          startIcon={<Save />}
        >
          {loading ? 'Kaydediliyor...' : 'Panel Ayarlarını Kaydet'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={loadPanelSettings}
          disabled={loading}
          startIcon={<Refresh />}
        >
          Yenile
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default PageSettings;
